import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Loader2, ArrowRight, AlertCircle, Play, RotateCcw, ScrollText, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useRun } from "@/context/RunContext";

// Pipeline stages in display order — mirrors the Intugleflow checkpoint sequence
const PIPELINE_STAGES = [
  { id: "SOURCE_LOADING",     label: "Loading source data",          desc: "ERP, CRM and Treasury" },
  { id: "STANDARDIZATION",    label: "Standardizing records",        desc: "AI code generation" },
  { id: "RECONCILIATION",     label: "Running reconciliation",       desc: "AI matching logic" },
  { id: "EXCEPTION_ANALYSIS", label: "Analysing exceptions",         desc: "Detecting mismatches" },
  { id: "DOCUMENT_RETRIEVAL", label: "Fetching invoice documents",   desc: "Intugle document retrieval" },
  { id: "AI_INVESTIGATION",   label: "AI investigation",             desc: "Root cause analysis" },
  { id: "REVIEW_READY",       label: "Assembling review queue",      desc: "Preparing human review" },
];

const STAGE_ORDER = PIPELINE_STAGES.map((s) => s.id);

function stageStateFor(stageId, currentStageId, isComplete) {
  if (isComplete) return "done";
  if (!currentStageId) return "pending";
  const currentIdx = STAGE_ORDER.indexOf(currentStageId);
  const thisIdx = STAGE_ORDER.indexOf(stageId);
  if (currentIdx === -1) return "pending";
  if (thisIdx < currentIdx) return "done";
  if (thisIdx === currentIdx) return "active";
  return "pending";
}

function StageTimeline({ currentStage, complete, stageHistory = [] }) {
  const activeId = currentStage?.stage;
  const detail = currentStage?.detail;

  // Elapsed timer — resets whenever the active stage changes
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    setElapsed(0);
    if (timerRef.current) clearInterval(timerRef.current);
    if (activeId && activeId !== "DONE" && !complete) {
      timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [activeId, complete]);

  const fmtSecs = (s) =>
    `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  // Build a lookup from stageHistory for persisted durations
  const historyMap = Object.fromEntries(stageHistory.map((h) => [h.stage, h]));

  return (
    <div className="mt-8 space-y-0">
      {PIPELINE_STAGES.map((s, i) => {
        const state = stageStateFor(s.id, activeId, complete);
        const isLast = i === PIPELINE_STAGES.length - 1;
        const hist = historyMap[s.id];

        return (
          <div key={s.id} className="relative flex gap-4">
            {/* Connector line */}
            {!isLast && (
              <div className="absolute left-[14px] top-[28px] w-px bottom-0 bg-zinc-100" />
            )}

            {/* Node icon */}
            <div className="relative z-10 mt-1 flex-shrink-0">
              {state === "done" ? (
                <span className="flex h-7 w-7 items-center justify-center rounded-full border border-emerald-300 bg-emerald-50">
                  <Check className="h-3.5 w-3.5 text-emerald-700" strokeWidth={3} />
                </span>
              ) : state === "active" ? (
                <span className="relative flex h-7 w-7 items-center justify-center rounded-full border border-blue-300 bg-blue-50">
                  <span className="absolute inset-0 animate-ping rounded-full border border-blue-400 opacity-50" />
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-600" strokeWidth={2.5} />
                </span>
              ) : (
                <span className="flex h-7 w-7 items-center justify-center rounded-full border border-zinc-200 bg-white">
                  <span className="h-1.5 w-1.5 rounded-full bg-zinc-300" />
                </span>
              )}
            </div>

            {/* Stage content */}
            <div className={`pb-5 flex-1 ${state === "pending" ? "opacity-35" : ""}`}>
              <div className="flex items-baseline justify-between gap-4">
                <p
                  className={`font-mono text-sm font-medium leading-7 ${
                    state === "active"
                      ? "text-blue-700"
                      : state === "done"
                      ? "text-zinc-900"
                      : "text-zinc-500"
                  }`}
                >
                  {s.label}
                </p>
                {/* Persisted duration on done stages */}
                {state === "done" && hist?.duration_s !== undefined && (
                  <span className="font-mono text-xs tabular-nums text-zinc-400">
                    {fmtSecs(hist.duration_s)}
                  </span>
                )}
              </div>
              {state === "active" ? (
                <>
                  {detail && (
                    <p className="mt-0.5 font-mono text-xs text-blue-500 italic">{detail}</p>
                  )}
                  <p className="mt-0.5 font-mono text-xs text-zinc-400">
                    {s.desc} · <span className="tabular-nums">{fmtSecs(elapsed)}</span>
                  </p>
                </>
              ) : (
                <p className="mt-0.5 font-mono text-xs text-zinc-400">{s.desc}</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const POLICY = [
  "Amount variance threshold: ₹1,000",
  "Matching basis: Company + Invoice",
  "Customer names normalized",
  "Duplicate invoice detection enabled",
  "Month-end timing differences recognized",
];

const SOURCES = [
  { name: "SAP S/4HANA", role: "ERP revenue postings" },
  { name: "Billing System", role: "CRM invoices" },
  { name: "Bank Statements", role: "Treasury settlements" },
];

export default function CloseRun() {
  const navigate = useNavigate();
  const {
    config,
    error,
    started,
    complete,
    start,
    reset,
    summary,
    period,
    available,
    runStatus,
    runError,
    reviewCases,
    runId,
    currentStage,
    stageHistory,
    runWallClock,
  } = useRun();
  const [policyOpen, setPolicyOpen] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(false);

  // Collapse the timeline when a new run starts
  useEffect(() => {
    if (!started) setTimelineOpen(false);
  }, [started]);

  if (error)
    return (
      <p className="text-sm text-red-600">
        Demo data is not available. Seed the workbook and reload.
      </p>
    );

  if (!config)
    return (
      <p className="flex items-center gap-2 text-sm text-zinc-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading close period…
      </p>
    );

  if (!available)
    return (
      <div className="max-w-3xl" data-testid="period-unavailable">
        <p className="font-mono text-xs uppercase tracking-[0.18em] text-zinc-400">
          Stage 01 · Close Run
        </p>
        <h1 className="mt-3 font-heading text-4xl font-medium tracking-tight text-zinc-900 sm:text-5xl">
          {period?.close_type} · {period?.label}
        </h1>
        <p className="mt-4 max-w-xl text-base text-zinc-500">
          No source extracts have been loaded for this close period yet.
        </p>
        <div className="mt-12 rounded-lg border border-dashed border-zinc-300 bg-white p-10">
          <h2 className="font-heading text-lg font-medium text-zinc-900">Period not loaded</h2>
          <p className="mt-2 max-w-xl text-sm text-zinc-500">
            {period?.range}. Select <span className="font-mono text-zinc-900">July 2026</span> in
            the close period selector to run the demo close.
          </p>
        </div>
      </div>
    );

  const handleStart = () => {
    if (!period || !summary) return;
    start({
      period_id: period.id,
      period_name: summary.period,
      start_date: period.start_date || "",
      end_date: period.end_date || "",
      company_code: "1000",
      close_type: (period.close_type || "Month End").toUpperCase().replace(/ /g, "_"),
      materiality: 1000,
    });
  };

  return (
    <div className="max-w-3xl">
      <div className="flex flex-wrap items-end justify-between gap-6 border-b border-zinc-200 pb-10">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.18em] text-zinc-400">
            Stage 01 · Close Run · {summary.close_type}
          </p>
          <h1 className="mt-3 font-heading text-4xl font-medium tracking-tight text-zinc-900 sm:text-5xl">
            {summary.close_type} Close
          </h1>
          <p className="mt-3 text-base text-zinc-500 md:text-lg" data-testid="page-period-line">
            {summary.period} · {period?.range} · revenue &amp; receivables reconciliation
          </p>
        </div>
        {started && runStatus && (
          <div className="text-right">
            <p
              className={`font-mono text-sm font-medium ${
                runStatus === "REVIEW_READY"
                  ? "text-emerald-700"
                  : runStatus === "FAILED"
                  ? "text-red-600"
                  : "text-blue-600"
              }`}
              data-testid="run-status-badge"
            >
              {runStatus === "PROCESSING"
                ? "IN PROGRESS"
                : runStatus === "REVIEW_READY"
                ? "REVIEW READY"
                : "FAILED"}
            </p>
            <p className="text-[11px] uppercase tracking-wide text-zinc-400">run status</p>
          </div>
        )}
      </div>

      {!started ? (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mt-12"
          data-testid="pre-run-panel"
        >
          <div className="rounded-lg border border-zinc-200 bg-white p-8 md:p-10">
            <h2 className="font-heading text-2xl font-medium tracking-tight text-zinc-900">
              Start {summary.close_type} Close Run
            </h2>
            <p className="mt-2 max-w-xl text-sm text-zinc-500">
              The run collects data from three systems, standardizes and merges it, applies
              deterministic reconciliation rules and routes exceptions to the AI investigation
              agent.
            </p>

            <dl className="mt-8 grid grid-cols-2 gap-x-10 gap-y-6 border-t border-zinc-100 pt-8 sm:grid-cols-3">
              <div>
                <dt className="text-[11px] uppercase tracking-wide text-zinc-400">Period</dt>
                <dd className="mt-1 font-mono text-base text-zinc-900" data-testid="pre-run-period">
                  {summary.period}
                </dd>
              </div>
              <div>
                <dt className="text-[11px] uppercase tracking-wide text-zinc-400">Close type</dt>
                <dd className="mt-1 font-mono text-base text-zinc-900" data-testid="pre-run-close-type">
                  {summary.close_type}
                </dd>
              </div>
              <div>
                <dt className="text-[11px] uppercase tracking-wide text-zinc-400">Company code</dt>
                <dd className="mt-1 font-mono text-base text-zinc-900">1000</dd>
              </div>
              <div>
                <dt className="text-[11px] uppercase tracking-wide text-zinc-400">Stages</dt>
                <dd className="mt-1 font-mono text-base text-zinc-900">
                  {config?.stages?.length ?? "—"}
                </dd>
              </div>
              <div>
                <dt className="text-[11px] uppercase tracking-wide text-zinc-400">Materiality</dt>
                <dd className="mt-1 font-mono text-base text-zinc-900">INR 1,000</dd>
              </div>
              <div>
                <dt className="text-[11px] uppercase tracking-wide text-zinc-400">
                  Reconciliation policy
                </dt>
                <dd className="mt-1">
                  <button
                    data-testid="view-policy"
                    onClick={() => setPolicyOpen(true)}
                    className="inline-flex items-center gap-1.5 font-mono text-base text-zinc-900 underline decoration-zinc-300 decoration-1 underline-offset-4 transition-colors duration-150 hover:decoration-zinc-900"
                  >
                    <ScrollText className="h-3.5 w-3.5" /> View policy
                  </button>
                </dd>
              </div>
              <div>
                <dt className="text-[11px] uppercase tracking-wide text-zinc-400">Status</dt>
                <dd className="mt-1 font-mono text-base text-zinc-500">Not started</dd>
              </div>
            </dl>

            <div className="mt-8 border-t border-zinc-100 pt-8">
              <p className="text-[11px] uppercase tracking-wide text-zinc-400">Source systems</p>
              <ul className="mt-4 space-y-2">
                {SOURCES.map((s) => (
                  <li key={s.name} className="flex items-baseline gap-3 text-sm">
                    <span className="h-1.5 w-1.5 translate-y-[-2px] rounded-full bg-zinc-300" />
                    <span className="font-medium text-zinc-900">{s.name}</span>
                    <span className="text-zinc-500">{s.role}</span>
                  </li>
                ))}
              </ul>
            </div>

            <Button
              data-testid="start-close-run"
              onClick={handleStart}
              className="group mt-10 rounded-full bg-zinc-900 px-6 py-5 text-sm font-medium text-white transition-colors duration-200 hover:bg-zinc-700"
            >
              <Play className="mr-2 h-4 w-4" /> Run {summary.close_type} Close
            </Button>
          </div>
        </motion.div>
      ) : (
        <>
          {/* PROCESSING — stage timeline */}
          {!complete && runStatus === "PROCESSING" && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="mt-12 rounded-lg border border-zinc-200 bg-white p-8 md:p-10"
              data-testid="processing-panel"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-mono text-xs uppercase tracking-wide text-zinc-400">
                    Pipeline running
                  </p>
                  <p className="mt-1 font-mono text-sm font-medium text-zinc-900">
                    {currentStage
                      ? currentStage.label
                      : "Starting pipeline…"}
                  </p>
                </div>
                {!currentStage && (
                  <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
                )}
              </div>

              <StageTimeline
                currentStage={currentStage}
                complete={false}
                stageHistory={stageHistory}
              />

              {runId && (
                <p className="mt-4 border-t border-zinc-100 pt-4 font-mono text-xs text-zinc-400">
                  Run ID: <span className="select-all text-zinc-500">{runId}</span>
                </p>
              )}
            </motion.div>
          )}

          {/* FAILED */}
          {runStatus === "FAILED" && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="mt-12 rounded-lg border border-red-200 bg-red-50 p-8 md:p-10"
              data-testid="failed-panel"
            >
              <div className="flex items-start gap-3">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
                <div>
                  <p className="font-mono text-sm font-medium text-red-900">
                    Reconciliation run failed
                  </p>
                  {runError?.component && (
                    <p className="mt-1 font-mono text-xs text-red-600">
                      Component: {runError.component}
                    </p>
                  )}
                  <p className="mt-1 text-sm text-red-700">
                    {runError?.message || runError || "Unknown error"}
                  </p>
                </div>
              </div>
              <button
                data-testid="reset-run"
                onClick={reset}
                className="mt-6 inline-flex items-center gap-1.5 rounded-full border border-red-200 px-5 py-2.5 text-sm text-red-700 transition-colors duration-150 hover:border-red-400 hover:text-red-900"
              >
                <RotateCcw className="h-3.5 w-3.5" /> Try again
              </button>
            </motion.div>
          )}

          {/* REVIEW_READY — completion banner + collapsible pipeline details */}
          <AnimatePresence>
            {complete && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, ease: "easeOut" }}
                className="mt-12 space-y-3"
                data-testid="completion-banner"
              >
                {/* Completion card */}
                <div className="rounded-lg border border-zinc-200 bg-white p-8 md:p-10">
                  <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-emerald-700">
                    <Check className="h-3.5 w-3.5" strokeWidth={3} /> run finished
                  </div>
                  <h2
                    className="mt-4 font-heading text-3xl font-medium tracking-tight text-zinc-900"
                    data-testid="completion-title"
                  >
                    {summary.title}
                  </h2>
                  {reviewCases && reviewCases.length > 0 && (
                    <p className="mt-3 font-mono text-sm text-zinc-700">
                      — {reviewCases.length} exception case
                      {reviewCases.length !== 1 ? "s" : ""} ready for human review
                    </p>
                  )}
                  <ul className="mt-3 space-y-2" data-testid="completion-lines">
                    {summary.lines?.map((l) => (
                      <li key={l} className="font-mono text-sm text-zinc-700">
                        — {l}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8 flex flex-wrap items-center gap-3">
                    <Button
                      data-testid="cta-review-reconciliation"
                      onClick={() => navigate("/reconciliation")}
                      className="group rounded-full bg-zinc-900 px-6 py-5 text-sm font-medium text-white transition-colors duration-200 hover:bg-zinc-700"
                    >
                      {summary.cta || "Review Reconciliation"}
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                    </Button>
                    <button
                      data-testid="reset-run"
                      onClick={reset}
                      className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 px-5 py-2.5 text-sm text-zinc-600 transition-colors duration-150 hover:border-zinc-400 hover:text-zinc-900"
                    >
                      <RotateCcw className="h-3.5 w-3.5" /> Reset run
                    </button>
                  </div>
                </div>

                {/* Collapsible pipeline run details */}
                <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
                  <button
                    onClick={() => setTimelineOpen((o) => !o)}
                    className="flex w-full items-center justify-between px-6 py-4 text-left transition-colors duration-150 hover:bg-zinc-50"
                  >
                    <div className="flex items-center gap-3">
                      {timelineOpen
                        ? <ChevronDown className="h-4 w-4 text-zinc-400" />
                        : <ChevronRight className="h-4 w-4 text-zinc-400" />
                      }
                      <span className="font-mono text-xs uppercase tracking-wide text-zinc-500">
                        Pipeline run details
                      </span>
                      <span className="font-mono text-xs text-zinc-400">
                        · {PIPELINE_STAGES.length} stages
                        {runWallClock != null && (
                          <> · {String(Math.floor(runWallClock / 60)).padStart(2, "0")}:{String(runWallClock % 60).padStart(2, "0")} total</>
                        )}
                      </span>
                    </div>
                  </button>

                  <AnimatePresence initial={false}>
                    {timelineOpen && (
                      <motion.div
                        key="timeline"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: "easeInOut" }}
                        className="overflow-hidden"
                      >
                        <div className="border-t border-zinc-100 px-6 pb-6">
                          <StageTimeline
                            currentStage={{ stage: "DONE", label: "Complete", detail: "" }}
                            complete={true}
                            stageHistory={stageHistory}
                          />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}

      <Dialog open={policyOpen} onOpenChange={setPolicyOpen}>
        <DialogContent data-testid="policy-modal" className="max-w-lg border-zinc-200 bg-white">
          <DialogHeader className="text-left">
            <DialogTitle className="font-heading text-lg font-medium tracking-tight text-zinc-900">
              Reconciliation Policy
            </DialogTitle>
            <DialogDescription className="text-sm text-zinc-500">
              Rules applied by the deterministic matching engine for this close.
            </DialogDescription>
          </DialogHeader>
          <ul className="mt-2 space-y-3" data-testid="policy-list">
            {POLICY.map((rule) => (
              <li key={rule} className="flex items-start gap-3 text-sm text-zinc-700">
                <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-700" strokeWidth={3} />
                <span className="font-mono">{rule}</span>
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>
    </div>
  );
}
